import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import * as z from 'zod';
import { DateTime } from 'luxon';
import { BookOpen, Globe } from 'lucide-react';

import styles from './home.module.css';

import { NAVBAR_ICON_URL } from '@/constants';
import { AppContext } from '@/app.context';

import { Modal } from '@shared/ui/modal';
import { AboutButton } from '@widgets/about';
import { Topbar as TopbarWidget } from '@shared/ui/topbar';

import { EyeIndicator } from '@pages/drafts';
import { InstallationDialog } from '@features/install-pwa';
import { InstallBtn } from '@features/install-pwa';

import { Context, Mode } from './home.context';
import { Magazines } from './sections/magazines/magazines';
import { Collections } from './sections/collections/collections';

const homeRouteApi = getRouteApi('/');

export const homeSearchSchema = z.object({
	section: z.custom<Mode>().default('issues'),
	// Token auth's credential -- read by `app.router.tsx`'s `beforeLoad`
	// (`token-auth-guard.ts`'s `tokenAuthGuard`) on both this route and
	// `collections/$id`, so it's declared in each route's own schema
	// rather than owned by one.
	token: z.string().optional(),
});

type HomeSearch = z.infer<typeof homeSearchSchema>;

export const HomeView = () => {
	const router = useRouter();
	const { section }: HomeSearch = homeRouteApi.useSearch();
	const [showSubscribe, setShowSubscribe] = useState(false);
	const [showInstallation, setShowInstallation] = useState(false);

	const [mode, setMode] = useState<Mode>(section || 'issues');

	useEffect(() => {
		const key = 'subscription-dialog-exp';

		const validateExp = () => {
			const now = DateTime.now();
			if (!localStorage.getItem(key)) {
				const expirationDt = now.plus({ minutes: 10 });
				localStorage.setItem(key, expirationDt.toISO());
				setShowSubscribe(true);
				return;
			}

			const expirationDt = DateTime.fromJSDate(
				new Date(`${localStorage.getItem(key)}`),
			);

			if (now >= expirationDt) {
				localStorage.removeItem(key);
				validateExp();
			}
		};

		validateExp();
	}, []);

	// `replace: true` is load-bearing, not a style preference: this effect
	// re-runs on every mount of `HomeView`, including landing back on `/` via
	// the browser's own back button. Without `replace`, each of those mounts
	// *pushed* a fresh history entry instead of updating the existing one --
	// so pressing back a second time didn't go further back through the
	// reader's actual navigation history, it unwound through these synthetic
	// entries this effect kept adding, which is how `issues -> preview ->
	// issue -> back -> back` could land on whatever `section` happened to be
	// in a stale entry (reported as landing on `collections` unexpectedly)
	// instead of the `issues` tab the reader actually came from.
	useEffect(() => {
		const currentSearch = router.latestLocation.search;
		router.navigate({
			to: '/',
			search: {
				...currentSearch,
				section: mode,
			},
			replace: true,
		});
	}, [mode, router]);
	const contextValue = useMemo(
		() => ({
			mode,
			setMode,
			setShowSubscribe,
			showSubscribe,
			setShowInstallation,
		}),
		[mode, showSubscribe],
	);

	return (
		<Context.Provider value={contextValue}>
			<SubscriptionDialog />
			<InstallationDialog
				setShowInstallation={setShowInstallation}
				show={showInstallation}
			/>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
					<Navbar />
				</div>
			</div>
		</Context.Provider>
	);
};

function Topbar() {
	const { setShowInstallation } = useContext(Context);
	const { isInstalled } = useContext(AppContext);
	return (
		<TopbarWidget
			className={styles['topbar']}
			left={
				<>
					<AboutButton />
					<EyeIndicator />
				</>
			}
			center={
				<div className={styles['title']}>
					<img src={NAVBAR_ICON_URL} alt="Logo" />
				</div>
			}
			right={
				<>
					<ButtonBar />
					{!isInstalled ? (
						<InstallBtn
							onClick={() => {
								setShowInstallation(true);
							}}
						/>
					) : null}
				</>
			}
		/>
	);
}

function Content() {
	const { mode } = useContext(Context);
	return (
		<div className={styles['content']}>
			{mode === 'collections' ? <Collections /> : <Magazines />}
		</div>
	);
}

function Navbar() {
	// The class is load-bearing, not decoration. `@shared/ui/topbar` renders
	// its own root as a `<nav>` too, so this page's bottom-navigation styling
	// -- written as bare `.view nav ...` element selectors -- was also matching
	// the topbar at the top of the very same grid. That leaked five rules onto
	// it, including `border-top: 1px solid #c3c3c3` (the grey line above the
	// topbar the user has reported repeatedly, home-only) and
	// `margin-bottom: var(--safe-area-inset)` on every topbar button -- 0 in a
	// browser tab, 34px in an installed iOS PWA, which is why the misalignment
	// only ever reproduced once installed. Scoping the bottom nav to its own
	// class is what makes `.view .navbar ...` unable to reach the topbar.
	return (
		<nav className={styles['navbar']}>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	const { mode, setMode } = useContext(Context);

	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
			return;
		}

		root.style.setProperty('--bottom-bar-height', '0px');
	}, [ref]);

	return (
		<div ref={ref} className={styles['button-bar']}>
			<button
				type={'button'}
				key={'collections'}
				title="Collections"
				data-text="Collections"
				onClick={() => {
					setMode('collections');
				}}
				className={[
					mode === 'collections' ? styles['active'] : '',
					'tooltip',
					'down',
				].join(' ')}
			>
				<Globe color="var(--topbar-buttons-stroke)" />
				Collections
			</button>
			<button
				type={'button'}
				key={'issues'}
				title="Issues"
				data-text="Issues"
				onClick={() => {
					setMode('issues');
				}}
				className={[
					mode === 'issues' ? styles['active'] : '',
					'tooltip',
					'down',
				].join(' ')}
			>
				<BookOpen color="var(--topbar-buttons-stroke)" />
				Issues
			</button>
		</div>
	);
}

function SubscriptionDialog() {
	const { showSubscribe, setShowSubscribe } = useContext(Context);
	return (
		<Modal
			hasCloseBtn={true}
			classNames={[styles['subscription-dialog-container']]}
			isOpen={showSubscribe}
			onClose={() => {
				setShowSubscribe(false);
			}}
		>
			<div className={styles['subscription-dialog']}>
				<h3>
					Independent investment research, curated for financial
					professionals
				</h3>
				<p>
					Uncover new investing insights with a subscription to
					Morningstar magazine. Four times a year, you&apos;ll get the
					best of our analysis, commentary, and original research
					delivered to you for free.
				</p>
				<a
					href="https://www.morningstar.com/business/insights/magazine"
					target={'_blank'}
					rel="noreferrer"
				>
					Subscribe Now
				</a>
			</div>
		</Modal>
	);
}

export default HomeView;
